import React from "react";
import { useTranslate } from "#src/hooks";
import { notification, FormInstance, Form, Upload } from "antd";
import { UploadSimpleIcon } from "@phosphor-icons/react";
import { parseCsvEmails } from "@libertify/ui";
import styled from "styled-components";

const FONT_INTER = '"Inter Variable", "Inter", sans-serif';

interface VideoShareEmailConfigProps {
  initialEmails?: string[];
  form?: FormInstance;
  border?: boolean;
}

export const VideoShareEmailConfigComponent: React.FC<
  VideoShareEmailConfigProps
> = ({ initialEmails = [], form }) => {
  const t = useTranslate();

  const handleFileUpload = async (file: File) => {
    try {
      const csvEmails = await parseCsvEmails(file);

      if (csvEmails.length === 0) {
        notification.error({
          message: t("projects.components.VideoShareEmailConfig.csvError"),
          description: t(
            "projects.components.VideoShareEmailConfig.noEmailsFound"
          ),
          placement: "bottomLeft",
        });
        return false;
      }

      const existingEmailsString = form?.getFieldValue("emails") || "";
      const existingEmails = existingEmailsString
        .split(/[,\n]/)
        .map((email: string) => email.trim())
        .filter((email: string) => email.length > 0);

      const allEmails = [...existingEmails, ...csvEmails];
      const uniqueEmails = Array.from(new Set(allEmails));

      form?.setFieldsValue({ emails: uniqueEmails.join(",\n") });

      notification.success({
        message: t("projects.components.VideoShareEmailConfig.csvSuccess"),
        description: t(
          "projects.components.VideoShareEmailConfig.csvEmailsImported",
          { count: csvEmails.length }
        ),
        placement: "bottomLeft",
      });

      return true;
    } catch (error) {
      console.error("Error parsing CSV file:", error);
      notification.error({
        message: t("projects.components.VideoShareEmailConfig.csvError"),
        description:
          error instanceof Error
            ? error.message
            : t("projects.components.VideoShareEmailConfig.csvParseError"),
        placement: "bottomLeft",
      });
      return false;
    }
  };

  const uploadProps = {
    name: "file",
    accept: ".csv",
    multiple: false,
    showUploadList: false,
    beforeUpload: handleFileUpload,
  };

  return (
    <Wrapper>
      <Form
        form={form}
        layout="vertical"
        initialValues={initialEmails}
        name="emailConfigForm"
      >
        <EmailLabel htmlFor="emailConfigForm_emails">
          {t("projects.components.VideoShareEmailConfig.enterEmails")}
        </EmailLabel>
        <Form.Item name="emails" noStyle>
          <StyledTextarea
            id="emailConfigForm_emails"
            data-testid="email-textarea"
            rows={4}
            placeholder={t(
              "projects.components.VideoShareEmailConfig.emailPlaceholder"
            )}
            spellCheck={false}
          />
        </Form.Item>
      </Form>

      <OrDivider>{t("projects.components.VideoShareEmailConfig.or")}</OrDivider>

      <Upload {...uploadProps}>
        <UploadBtn type="button">
          <UploadSimpleIcon size={14} />
          {t("projects.components.VideoShareEmailConfig.uploadCSV")}
        </UploadBtn>
      </Upload>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const EmailLabel = styled.label`
  font-family: ${FONT_INTER};
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 6px;
`;

const StyledTextarea = styled.textarea`
  width: 100%;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  font-family: ${FONT_INTER};
  font-size: 13px;
  color: #374151;
  padding: 10px 12px;
  resize: none;
  outline: none;
  background: #f9fafb;
  box-sizing: border-box;
  transition: border-color 0.15s;

  &:focus {
    border-color: #14b8a6;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const OrDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: ${FONT_INTER};
  font-size: 12px;
  color: #d1d5db;
  margin: 12px 0;

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: #f3f4f6;
  }
`;

const UploadBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 7px 14px;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  background: white;
  font-family: ${FONT_INTER};
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  transition:
    border-color 0.15s,
    background 0.15s;

  &:hover {
    border-color: #14b8a6;
    background: #f0fdfb;
  }
`;
